import React, { useState, useEffect } from "react";
import "../../../assets/scss/TradingAccountsModal.scss";
import {
  fetchClientIp,
  postUserLogin,
  postInstantDemo,
  buildPasswordLoginBody,
  buildInstantDemoBody,
  buildTradingAccountFromLoginResponse,
  resolveInstantDemoDisplayLogin,
  mapAxiosErrorToMessage,
  postResetPassword,
  SERVER_LIVE,
} from "../../../services/authService";
import { errorLog } from "../../../utils/logger";

/** Same rules as web `ChangePassword` / `MobileNewChangePassword` (Yup). */
const NEW_PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/;

// ── Connect to Account Panel — uses services/authService (shared with Login.js) ─
function ConnectPanel({ onConnected, isAdminModal = false }) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [instantLoading, setInstantLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchClientIp();
  }, []);

  const handleLogin = async () => {
    if (!login || !password) {
      setError("Please enter login and password");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const userIp = await fetchClientIp();
      const body = buildPasswordLoginBody({
        username: login.trim(),
        password,
        userIp,
        isUserLogin: !isAdminModal,
      });
      const data = await postUserLogin(body);
      onConnected(buildTradingAccountFromLoginResponse(data, login.trim(), false));
    } catch (err) {
      errorLog("TradingAccountsModal login:", err?.message);
      setError(mapAxiosErrorToMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleInstantDemo = async () => {
    setInstantLoading(true);
    setError("");
    try {
      const userIp = await fetchClientIp();
      const body = buildInstantDemoBody({ userIp, isUserLogin: true });
      const data = await postInstantDemo(body);
      const demoLogin = resolveInstantDemoDisplayLogin(data);
      onConnected(buildTradingAccountFromLoginResponse(data, demoLogin || "demo", true));
    } catch (err) {
      errorLog("TradingAccountsModal InstantDemo:", err?.message);
      setError(mapAxiosErrorToMessage(err));
    } finally {
      setInstantLoading(false);
    }
  };

  return (
    <div className="tam-panel">
      <h3 className="tam-panel__title">Login</h3>

      <div className="tam-field">
        <label>Login</label>
        <input type="text" placeholder="Enter login" value={login}
          onChange={e => setLogin(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleLogin()} />
      </div>

      <div className="tam-field">
        <label>Password</label>
        <div className="tam-pw-wrap">
          <input
            type={showPw ? "text" : "password"}
            placeholder="Enter password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleLogin()}
          />
          <span className="tam-eye" onClick={() => setShowPw(v => !v)}>
            {showPw ? "🙈" : "👁️"}
          </span>
        </div>
      </div>

      {isAdminModal && (
        <div className="tam-field">
          <label>Server</label>
          <input type="text" value={SERVER_LIVE} disabled
            style={{ opacity: 0.7, cursor: "not-allowed", background: "#f5f5f5" }} />
        </div>
      )}

      {!isAdminModal && (
        <p className="tam-field-hint" style={{ fontSize: 12, color: "#666", margin: "0 0 8px" }}>
          Real account login only. Use the login screen <strong>Instant Demo</strong> for a demo user.
        </p>
      )}

      {error && <div className="tam-error">{error}</div>}

      <div className="tam-btn-row">
        <button
          className="tam-btn-primary"
          onClick={handleLogin}
          disabled={loading || instantLoading}
        >
          {loading ? "Please wait…" : "Connect to account"}
        </button>
        {!isAdminModal && (
          <button
            type="button"
            className="tam-btn-ghost"
            onClick={handleInstantDemo}
            disabled={loading || instantLoading}
          >
            {instantLoading ? "Please wait…" : "Instant Demo"}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Open Demo Account Panel ───────────────────────────────────────────────────
function DemoPanel({ onCreated }) {
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    hedge: true, accountType: "Forex Hedged USD",
    deposit: "100000", leverage: "100", agreed: false,
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleOpen = () => {
    if (!form.firstName || !form.email) { setError("Name and email are required."); return; }
    if (!form.agreed) { setError("Please agree to the terms and conditions."); return; }
    setLoading(true); setError("");
    setTimeout(() => { setLoading(false); onCreated(form); }, 1400);
  };

  return (
    <div className="tam-panel">
      <h3 className="tam-panel__title">Open Demo account</h3>

      <div className="tam-company-tag">
        <span className="tam-company-label">Company</span>
        <span className="tam-company-name">FxBazzar</span>
      </div>

      <div className="tam-field">
        <label>Your Name</label>
        <div className="tam-row">
          <input type="text" placeholder="Name"    value={form.firstName} onChange={e => set("firstName", e.target.value)} />
          <input type="text" placeholder="Surname" value={form.lastName}  onChange={e => set("lastName",  e.target.value)} />
        </div>
      </div>

      <div className="tam-field">
        <label>Email</label>
        <input type="email" placeholder="Email" value={form.email} onChange={e => set("email", e.target.value)} />
      </div>

      <div className="tam-field">
        <label>Mobile phone</label>
        <input type="tel" placeholder="Phone number" value={form.phone} onChange={e => set("phone", e.target.value)} />
      </div>

      <div className="tam-checkbox-row">
        <input type="checkbox" id="hedge" checked={form.hedge} onChange={e => set("hedge", e.target.checked)} />
        <label htmlFor="hedge">Use hedge in trading</label>
      </div>

      <div className="tam-field">
        <label>Account type</label>
        <select value={form.accountType} onChange={e => set("accountType", e.target.value)}>
          <option>Forex Hedged USD</option>
          <option>Forex Standard USD</option>
          <option>Crypto USD</option>
        </select>
      </div>

      <div className="tam-row tam-row--gap">
        <div className="tam-field" style={{ flex: 1 }}>
          <label>Deposit</label>
          <select value={form.deposit} onChange={e => set("deposit", e.target.value)}>
            {["10000","25000","50000","100000","200000"].map(v =>
              <option key={v} value={v}>{parseInt(v).toLocaleString()} USD</option>
            )}
          </select>
        </div>
        <div className="tam-field" style={{ width: 110 }}>
          <label>Leverage</label>
          <select value={form.leverage} onChange={e => set("leverage", e.target.value)}>
            {["50","100","200","500"].map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </div>

      <div className="tam-checkbox-row">
        <input type="checkbox" id="terms" checked={form.agreed} onChange={e => set("agreed", e.target.checked)} />
        <label htmlFor="terms">
          I agree with the <a href="#terms" className="tam-link">terms and conditions</a> and data protection policy
        </label>
      </div>

      {error && <div className="tam-error">{error}</div>}

      <div className="tam-footer-link">
        <a href="https://fxbazzar.com" target="_blank" rel="noreferrer" className="tam-link">www.fxbazzar.com</a>
      </div>

      <button className="tam-btn-primary" onClick={handleOpen} disabled={loading || !form.agreed}>
        {loading ? "Creating…" : "Open Demo account"}
      </button>
    </div>
  );
}

// ── Account Detail Panel ──────────────────────────────────────────────────────
function AccountDetail({ account, onConnect, onSignOut, onAccountPatch }) {
  const [view,  setView]  = useState("detail");
  const [pw,    setPw]    = useState({ current: "", newPw: "", confirm: "" });
  const [pwErr, setPwErr] = useState("");
  const [pwOk,  setPwOk]  = useState(false);
  const [pwLoading, setPwLoading] = useState(false);

  const cancelChangePw = () => {
    setView("detail");
    setPwErr("");
    setPwOk(false);
    setPw({ current: "", newPw: "", confirm: "" });
  };

  const handleChangePw = async () => {
    if (!pw.current) { setPwErr("Enter current password."); return; }
    if (!NEW_PASSWORD_REGEX.test(pw.newPw)) {
      setPwErr(
        "New password must be at least 8 characters and include uppercase, lowercase, a number, and a special character (!@#$%^&*)."
      );
      return;
    }
    if (pw.newPw !== pw.confirm) { setPwErr("Passwords don't match."); return; }
    if (!account.token) {
      setPwErr("Session token missing. Connect again from the Login tab.");
      return;
    }

    setPwErr("");
    setPwOk(false);
    setPwLoading(true);
    try {
      const payload = {
        oldPassword: pw.current,
        password: pw.newPw,
        confirmPassword: pw.confirm,
        userName: String(account.login || "").trim(),
      };

      const data = await postResetPassword(account.token, payload);

      if (data?.status !== 200) {
        throw new Error(data?.message || "Password change failed");
      }

      const apiData = data.data || {};
      const newToken =
        apiData.tokens?.accessToken || apiData.token || apiData.accessToken;
      if (newToken && typeof onAccountPatch === "function") {
        onAccountPatch(account.id, { token: newToken });
      }

      setPwOk(true);
      setPw({ current: "", newPw: "", confirm: "" });
      setTimeout(() => {
        setPwOk(false);
        setView("detail");
      }, 2200);
    } catch (err) {
      errorLog("TradingAccountsModal reset-password:", err?.message);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.title ||
        mapAxiosErrorToMessage(err);
      setPwErr(msg);
    } finally {
      setPwLoading(false);
    }
  };

  if (view === "changePassword") return (
    <div className="tam-panel">
      <h3 className="tam-panel__title">Change Password</h3>
      <div className="tam-detail-row"><span>Login</span><strong>{account.login}</strong></div>
      <div className="tam-field">
        <label>Current Password</label>
        <input type="password" placeholder="Enter current password"
          value={pw.current} onChange={e => setPw(p => ({...p, current: e.target.value}))}
          disabled={pwLoading} autoComplete="current-password" />
      </div>
      <div className="tam-field">
        <label>New Password</label>
        <input type="password" placeholder="Enter new password"
          value={pw.newPw} onChange={e => setPw(p => ({...p, newPw: e.target.value}))}
          disabled={pwLoading} autoComplete="new-password" />
      </div>
      <div className="tam-field">
        <label>Confirm Password</label>
        <input type="password" placeholder="Confirm Password"
          value={pw.confirm} onChange={e => setPw(p => ({...p, confirm: e.target.value}))}
          disabled={pwLoading} autoComplete="new-password" />
      </div>
      <p style={{ fontSize: 12, color: "#666", margin: "0 0 8px" }}>
        After a successful change, sign in with your <strong>new</strong> password. The old password will no longer work.
      </p>
      {pwErr && <div className="tam-error">{pwErr}</div>}
      {pwOk  && <div className="tam-success">Password updated on the server. Use your new password next time you sign in.</div>}
      <div className="tam-btn-row">
        <button className="tam-btn-primary" onClick={handleChangePw} disabled={pwLoading}>
          {pwLoading ? "Updating…" : "Change Password"}
        </button>
        <button type="button" className="tam-btn-ghost" onClick={cancelChangePw} disabled={pwLoading}>Cancel</button>
      </div>
    </div>
  );

  return (
    <div className="tam-panel">
      <div className="tam-account-hero">
        <div>
          <div className="tam-account-hero__name">{account.name || account.login}</div>
          <div className="tam-account-hero__sub">
            {account.login} · {account.server} · {account.type}
            {account.isDemo && <span className="tam-demo-tag">DEMO</span>}
          </div>
        </div>
        <div className="tam-account-hero__balance">
          {account.balance
            ? `${Number(account.balance).toLocaleString()} ${account.currency || "USD"}`
            : `— ${account.currency || "USD"}`}
        </div>
      </div>

      <div className="tam-detail-rows">
        <div className="tam-detail-row">
          <span>Name</span>
          <strong>{account.name || "—"}</strong>
        </div>
        <div className="tam-detail-row">
          <span>Server</span>
          <strong>{account.server}</strong>
        </div>
        <div className="tam-detail-row">
          <span>Login</span>
          <strong>{account.login}</strong>
        </div>
        {account.userCode && (
          <div className="tam-detail-row">
            <span>User Code</span>
            <strong>{account.userCode}</strong>
          </div>
        )}
        <div className="tam-detail-row">
          <span>Password</span>
          <div className="tam-badge-row">
            {account.isDemo ? (
              <span style={{ fontSize: 12, color: "#666" }}>Instant Demo has no password to change.</span>
            ) : account.token ? (
              <>
                <span className="tam-badge tam-badge--green">Saved</span>
                <button type="button" className="tam-link-btn" onClick={() => setView("changePassword")}>
                  Change password
                </button>
              </>
            ) : (
              <span style={{ fontSize: 12, color: "#666" }}>Reconnect from Login to change password.</span>
            )}
          </div>
        </div>
      </div>

      <div className="tam-checkbox-row" style={{ margin: "14px 0 18px" }}>
        <input type="checkbox" id="savepw" defaultChecked />
        <label htmlFor="savepw">Save password</label>
      </div>

      <div className="tam-btn-row">
        <button className="tam-btn-primary" onClick={() => onConnect(account)}>Connect to account</button>
        <button className="tam-btn-danger"  onClick={() => onSignOut(account)}>Sign out</button>
      </div>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────
// ✅ FIX: Added onAccountSwitch prop — called whenever a new account becomes active
export default function TradingAccountsModal({ onClose, user, onLogout, onAccountSwitch }) {

  const isAdminModal = user?.isAdminLogin === true;

  const [tab, setTab] = useState("login");
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [toast, setToast] = useState("");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2800);
  };

  const handleConnected = (info) => {
    setAccounts(prev => {
      const exists = prev.find(
        a => a.login === info.login && a.server === info.server
      );

      let updatedAccounts;

      if (!exists) {
        const newAcc = {
          id: Date.now(),
          name: info.name || "",
          login: info.login,
          server: info.server,
          balance: info.balance || null,
          currency: info.currency || "USD",
          userCode: info.userCode || "",
          token: info.token || "",
          userId: info.userId ?? 0,
          type: "Hedge",
          active: true,
          isDemo: info.isDemo,
          isMasterRole: info.isMasterRole !== false,
        };

        updatedAccounts = [
          ...prev.map(a => ({ ...a, active: false })),
          newAcc
        ];

        setSelectedAccount(newAcc);

        // ✅ FIX: Notify parent of newly connected account
        if (typeof onAccountSwitch === "function") {
          onAccountSwitch(newAcc);
        }
      } else {
        updatedAccounts = prev.map(a => ({
          ...a,
          active: a.login === info.login && a.server === info.server
        }));

        const activeAcc = updatedAccounts.find(a => a.active);
        setSelectedAccount(activeAcc);

        // ✅ FIX: Notify parent of switched account
        if (typeof onAccountSwitch === "function" && activeAcc) {
          onAccountSwitch(activeAcc);
        }
      }

      return updatedAccounts;
    });

    showToast(`✅ Connected — ${info.login} (${info.server})`);
    setTab("accounts");
  };

  const handleConnect = (account) => {
    setAccounts(a => a.map(acc => ({ ...acc, active: acc.id === account.id })));
    const activeAcc = { ...account, active: true };
    setSelectedAccount(activeAcc);
    showToast(`✅ Switched to ${account.login}`);

    // ✅ FIX: Notify parent when switching between saved accounts
    if (typeof onAccountSwitch === "function") {
      onAccountSwitch(activeAcc);
    }
  };

  const handleAccountPatch = (accountId, patch) => {
    setAccounts(prev => {
      const next = prev.map(a => (a.id === accountId ? { ...a, ...patch } : a));
      const updated = next.find(a => a.id === accountId);
      if (updated?.active && typeof onAccountSwitch === "function") {
        Promise.resolve().then(() => onAccountSwitch(updated));
      }
      return next;
    });
    setSelectedAccount(s =>
      s && s.id === accountId ? { ...s, ...patch } : s
    );
  };

  const handleSignOut = (account) => {
    if (account.active) {
      showToast("Signing out…");
      setTimeout(() => {
        onClose();
        if (typeof onLogout === "function") onLogout();
      }, 800);
    } else {
      const remaining = accounts.filter(acc => acc.id !== account.id);
      setAccounts(remaining);
      setSelectedAccount(remaining.find(a => a.active) || remaining[0] || null);
      showToast(`Removed account ${account.login}`);
    }
  };

  return (
    <div className="tam-overlay" onClick={onClose}>
      <div className="tam-modal" onClick={e => e.stopPropagation()}>

        <div className="tam-modal__header">
          <span className="tam-modal__title">Trading accounts: FxBazzar Ltd.</span>
          <button className="tam-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="tam-modal__body">
          <div className="tam-sidebar">
            <div className="tam-tabs">
              <button className={`tam-tab ${tab==="login"    ? "active":""}`} onClick={() => setTab("login")}>Login / Register</button>
              <button className={`tam-tab ${tab==="accounts" ? "active":""}`} onClick={() => setTab("accounts")}>Accounts</button>
            </div>

            {tab === "login" && (
              <div className="tam-nav" style={{ padding: "10px 0" }}>
                <div className="tam-nav-item active">
                  <span className="tam-nav-icon">🔐</span>
                  <span>Login</span>
                </div>
              </div>
            )}

            {tab === "accounts" && (
              <div className="tam-account-list">
                {accounts.length === 0 && (
                  <div style={{ padding:"16px 12px", fontSize:12, color:"#999", textAlign:"center" }}>
                    No accounts yet.<br />Connect one from Login tab.
                  </div>
                )}
                {accounts.map(acc => (
                  <div
                    key={acc.id}
                    className={`tam-account-item ${selectedAccount?.id === acc.id ? "selected":""}`}
                    onClick={() => setSelectedAccount(acc)}
                  >
                    <div className="tam-account-item__avatar">
                      {(acc.name || acc.login).split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase()}
                    </div>
                    <div className="tam-account-item__info">
                      <div className="tam-account-item__name">
                        {acc.name || acc.login}
                        {acc.isDemo && <span className="tam-demo-tag">DEMO</span>}
                      </div>
                      <div className="tam-account-item__sub">
                        {acc.login}
                        {acc.balance
                          ? ` · ${Number(acc.balance).toLocaleString()} ${acc.currency}`
                          : ""}
                      </div>
                    </div>
                    {acc.active && <span className="tam-active-dot" />}
                  </div>
                ))}
              </div>
            )}

            <div className="tam-sidebar__footer">
              <p>© 2000 – 2026, FxBazzar Ltd.</p>
              <a href="#eula" className="tam-link">End-User License Agreement</a>
            </div>
          </div>

          <div className="tam-content">
            {tab==="login" && (
              <ConnectPanel onConnected={handleConnected} isAdminModal={isAdminModal} />
            )}
            {tab==="accounts" && selectedAccount && (
              <AccountDetail
                account={selectedAccount}
                onConnect={handleConnect}
                onSignOut={handleSignOut}
                onAccountPatch={handleAccountPatch}
              />
            )}
            {tab==="accounts" && !selectedAccount && (
              <div className="tam-empty">
                {accounts.length === 0
                  ? "No accounts yet. Go to Login tab to connect."
                  : "Select an account from the list."}
              </div>
            )}
          </div>
        </div>

        {toast && <div className="tam-toast">{toast}</div>}
      </div>
    </div>
  );
}